import { useState, FC } from "react";
import { createModalConfig, ModalConfig } from "../../../Models/ModalConfig";

import {
  DEFAULT_HANDLER_CONFIG,
  AddressBookIndexContext,
  AddressBookIndexContextValue,
  AddressBookIndexProviderProps,
} from "./";

/* ===========================================================================
 * PROVIDER: AddressBookIndexProvider
 * =========================================================================== */

/**
 * @component AddressBookIndexProvider
 *
 * Provides configuration and UI state for the AddressBook index view.
 * This top-level context manages:
 * - Modal configuration state
 * - Handler configuration (registry, billing, defaults)
 * - Owner and service metadata
 * - Optional lifecycle callback
 *
 * It acts as a lightweight “environment provider” for all components related
 * to listing, filtering, and managing AddressBook records.
 *
 * @param {AddressBookIndexProviderProps} props - Provider configuration.
 * @returns {JSX.Element} The wrapped children with context applied.
 */
export const AddressBookIndexProvider: FC<AddressBookIndexProviderProps> = ({
  service = "registry",
  ownerId,
  myAddress = false,
  children,
  callback,
  handlerConfig: incomingHandlerConfig,
}) => {
  /**
   * Final handler configuration after merging defaults with incoming values.
   * Ensures all required flags and structures are always present.
   */
  const handlerConfig = incomingHandlerConfig
    ? { ...DEFAULT_HANDLER_CONFIG, ...incomingHandlerConfig }
    : DEFAULT_HANDLER_CONFIG;

  /**
   * Holds configuration for modals displayed within the AddressBook index UI.
   * Includes open/closed state, payload, and contextual metadata.
   */
  const [modalConfig, setModalConfig] = useState<ModalConfig>(
    createModalConfig()
  );

  /**
   * Aggregated context value passed to all consumers.
   * Combines handler configuration settings, UI state, metadata, and callbacks.
   */
  const contextValue: AddressBookIndexContextValue = {
    service,
    ownerId,
    myAddress,
    ...handlerConfig,
    modalConfig,
    setModalConfig,
    callback,
  };

  return (
    <AddressBookIndexContext.Provider value={contextValue}>
      {children}
    </AddressBookIndexContext.Provider>
  );
};
